import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Card,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TablePagination,
  TableRow,
  Paper,
  Button,
  TableHead,
  IconButton,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Tabs,
  Tab,
  Chip,
} from "@mui/material";
import { Link } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { API_ENDPOINTS } from "../../../config/api";
import { useAuth } from "../../../context/AuthContext";

const API_URL = API_ENDPOINTS.DEALER_QUOTATIONS;

const DealerQuotationsList = () => {
  const [quotations, setQuotations] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [activeTab, setActiveTab] = useState(0); // 0: All, 1: Pending, 2: Finalized
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { user } = useAuth();
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success", // 'success' | 'error'
  });
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    id: null,
  });

  useEffect(() => {
    fetchQuotations();
  }, []);

  const fetchQuotations = async () => {
    try {
      const response = await axios.get(API_URL.BASE);
      const data = response.data;
      let quotationsArray = [];
      if (Array.isArray(data)) {
        quotationsArray = data;
      } else if (data && Array.isArray(data.quotations)) {
        quotationsArray = data.quotations;
      }
      setQuotations(quotationsArray);
      
      // Debug: Log unique status values to see what we're working with
      const uniqueStatuses = [...new Set(quotationsArray.map(q => q.status).filter(Boolean))];
      console.log('Available status values:', uniqueStatuses);
    } catch (error) {
      console.error("Error fetching quotations:", error);
      setQuotations([]);
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleDeleteClick = (id) => {
    setDeleteDialog({ open: true, id });
  };

  const handleDeleteConfirm = async () => {
    const id = deleteDialog.id;
    setDeleteDialog({ open: false, id: null });
    try {
      await axios.delete(API_URL.DELETE(id));
      setQuotations((prevQuotations) => prevQuotations.filter((quotation) => quotation.id !== id));
      setSnackbar({
        open: true,
        message: "Quotation deleted successfully!",
        severity: "success",
      });
    } catch (error) {
      let userMessage = "Failed to delete dealer quotation.";
      if (
        error.response?.data?.message &&
        error.response.data.message.includes("foreign key constraint fails")
      ) {
        userMessage =
          "Cannot delete this dealer quotation because it is assigned to other records. Please reassign or remove those records first.";
      }
      setSnackbar({
        open: true,
        message: userMessage,
        severity: "error",
      });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, id: null });
  };

  const handleTogglePublish = async (id, currentStatus) => {
    try {
      await axios.put(API_URL.EDIT(id), { publish: !currentStatus });
      setQuotations((prevQuotations) =>
        prevQuotations.map((quotation) =>
          quotation.id === id ? { ...quotation, publish: !currentStatus } : quotation
        )
      );
    } catch (error) {
      console.error("Error updating publish status:", error);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setPage(0); // Reset to first page when changing tabs
  };

  const formatStatusLabel = (status) => {
    if (!status) return '—';
    const s = String(status);
    return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
  };

  const renderStatusChip = (status) => {
    const statusStr = status || '';
    const normalized = statusStr.toLowerCase();
    const label = formatStatusLabel(statusStr);

    if (normalized.includes('pending')) {
      return (
        <Chip
          label={label}
          size="small"
          color="warning"
          sx={{ fontWeight: 600 }}
        />
      );
    }

    if (normalized.includes('finalized')) {
      return (
        <Chip
          label={label}
          size="small"
          color="success"
          sx={{ fontWeight: 600, color: 'common.white' }}
        />
      );
    }

    return (
      <Chip
        label={label}
        size="small"
        sx={{ fontWeight: 600 }}
      />
    );
  };

  const filteredQuotations = Array.isArray(quotations)
    ? quotations.filter((quotation) => {
        const searchTermLower = searchTerm.toLowerCase();
        const companyName = quotation.app_user?.company_name?.toLowerCase() || '';
        const quotationDate = new Date(quotation.created_at);
        
        // Date range filter
        const isInDateRange = (!startDate || quotationDate >= new Date(startDate)) && 
                            (!endDate || quotationDate <= new Date(endDate + 'T23:59:59.999'));
        
        // Tab-based status filter
        let matchesStatus = true;
        if (activeTab === 1) { // Pending tab
          // Check for various pending-like statuses
          matchesStatus = quotation.status && (
            quotation.status.toLowerCase().includes('pending') ||
            quotation.status.toLowerCase().includes('draft') ||
            quotation.status.toLowerCase().includes('new') ||
            quotation.status.toLowerCase().includes('open')
          );
        } else if (activeTab === 2) { // Finalized tab
          // Check for various finalized-like statuses
          matchesStatus = quotation.status && (
            quotation.status.toLowerCase().includes('finalized') ||
            quotation.status.toLowerCase().includes('completed') ||
            quotation.status.toLowerCase().includes('approved') ||
            quotation.status.toLowerCase().includes('closed') ||
            quotation.status.toLowerCase().includes('done')
          );
        }
        // activeTab === 0 (All tab) shows all statuses
        
        // Debug logging for first few items
        if (quotations.indexOf(quotation) < 3) {
          console.log(`Quotation ${quotation.id}: status="${quotation.status}", activeTab=${activeTab}, matchesStatus=${matchesStatus}`);
        }
        
        return companyName.includes(searchTermLower) && isInDateRange && matchesStatus;
      })
    : [];

  return (
    <Card sx={{ boxShadow: "none", borderRadius: "7px", mb: "25px", padding: "25px" }}>
      {/* <NavList /> */}
      <Box>
        {/* Status Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={activeTab} onChange={handleTabChange} aria-label="quotation status tabs">
            <Tab label={`All (${quotations.length})`} />
            <Tab label={`Pending (${quotations.filter(q => q.status && q.status.toLowerCase().includes('pending')).length})`} />
            <Tab label={`Finalized (${quotations.filter(q => q.status && q.status.toLowerCase().includes('finalized')).length})`} />
          </Tabs>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "space-between", mb: "25px", gap: 2, alignItems: "center" }}>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <div className="search-left">
              <label>Search</label>
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ padding: "8px", width: "200px", borderRadius: "5px", border: "1px solid #ccc" }}
            />
            </div>
            <TextField
              type="date"
              label="Start Date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              type="date"
              label="End Date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          {user.permissions.includes("add-dealerQuotations") && (
            <Link to="/ecommerce/create-dealer-quotation">
              <Button variant="contained" color="secondary">
                <AddIcon /> Add New Quotation
              </Button>
            
            </Link>
          )}
        </Box>

        <TableContainer className="custom-table" component={Paper}>
          <Table sx={{ minWidth: 1000 }} aria-label="Regions Table">
            <TableHead>
              <TableRow>
                <TableCell>S.No</TableCell>
                <TableCell>User</TableCell>
                
                <TableCell>Transporatation Charges</TableCell>
                <TableCell>Bending Charges</TableCell>
                <TableCell>GST Amount</TableCell>
                <TableCell>Total Amount</TableCell>
                <TableCell>Quotation Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredQuotations
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((quotation, index) => (
                  <TableRow key={quotation.id}>
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                   
                    <TableCell>{quotation.app_user.company_name} </TableCell>
                    <TableCell>{quotation.transport_charges}</TableCell>
                    <TableCell>
                      {quotation.bending_charges != null && quotation.bending_charges !== "" ? quotation.bending_charges : 0}
                    </TableCell>
                    <TableCell>{quotation.gst_amount}</TableCell>
                    <TableCell>{quotation.gst_amount}</TableCell>

                    <TableCell>{new Date(quotation.created_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</TableCell>
                    <TableCell>
                      {renderStatusChip(quotation.status)}
                    </TableCell>
                    <TableCell>
                      {user.permissions.includes("view-dealerQuotations") && (
                        <Link to={`/ecommerce/dealerquotations/view/${quotation.id}`}>
                          <IconButton color="info">
                            <VisibilityIcon />
                          </IconButton>
                        </Link>
                      )}
                      {user.permissions.includes("edit-dealerQuotations") && (
                        <Link to={`/ecommerce/edit-dealer-quotation/${quotation.id}`}>
                          <IconButton color="primary">
                            <EditIcon />
                          </IconButton>
                        </Link>
                      )}
                      {user.permissions.includes("delete-dealerQuotations") && (
                        <IconButton color="error" onClick={() => handleDeleteClick(quotation.id)}>
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, { label: "All", value: -1 }]}
                  count={filteredQuotations.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(event, newPage) => setPage(newPage)}
                  onRowsPerPageChange={(event) => {
                    setRowsPerPage(parseInt(event.target.value, 10));
                    setPage(0);
                  }}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>Delete Quotation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this dealer quotation?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000} // Adjust duration as needed
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default DealerQuotationsList;